/**
 * 管理后台外壳。
 * 替代老系统 mcs 的 admin/ 目录（88 个 JSP 页面）与 man-main.jsp 框架页。
 */

import { useEffect, useState } from 'react'

import type { RecordRow } from '../lib/adminApi'
import { MonitorPanel } from './admin/MonitorPanel'
import { RecordsPanel } from './admin/RecordsPanel'
import { RoomsPanel } from './admin/RoomsPanel'
import { UsersPanel } from './admin/UsersPanel'
import { PlaybackPage } from './Playback'

type Tab = 'monitor' | 'rooms' | 'users' | 'records'

const TABS: Array<{ id: Tab; label: string; hint: string }> = [
  { id: 'monitor', label: '运行监控', hint: '在线人数、活动房间、日志查询' },
  { id: 'rooms', label: '房间管理', hint: '房间配置、默认权限、主持人' },
  { id: 'users', label: '用户管理', hint: '账号、等级与权限' },
  { id: 'records', label: '录像管理', hint: '课堂录像点播与回放' },
]

interface Props {
  token: string
  account: string
  onExit: () => void
}

export function Admin({ token, account, onExit }: Props) {
  const [tab, setTab] = useState<Tab>('monitor')
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null)
  const [playing, setPlaying] = useState<RecordRow | null>(null)

  useEffect(() => {
    if (!toast) return
    const t = window.setTimeout(() => setToast(null), 4000)
    return () => window.clearTimeout(t)
  }, [toast])

  const onError = (msg: string) => setToast({ kind: 'err', msg })
  const onNotice = (msg: string) => setToast({ kind: 'ok', msg })

  if (playing) {
    return <PlaybackPage record={playing} onClose={() => setPlaying(null)} />
  }

  return (
    <div className="admin">
      <header className="adm-head">
        <div className="adm-brand">
          <strong>网梯直播系统</strong>
          <span className="dim">管理后台</span>
        </div>
        <nav className="adm-nav">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={tab === t.id ? 'on' : ''}
              title={t.hint}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="adm-user">
          <span>{account}</span>
          <button onClick={onExit}>退出</button>
        </div>
      </header>

      <main className="adm-body">
        {tab === 'monitor' && <MonitorPanel token={token} onError={onError} />}
        {tab === 'rooms' && (
          <RoomsPanel token={token} onError={onError} onNotice={onNotice} />
        )}
        {tab === 'users' && (
          <UsersPanel token={token} onError={onError} onNotice={onNotice} />
        )}
        {tab === 'records' && (
          <RecordsPanel
            token={token}
            onError={onError}
            onNotice={onNotice}
            onPlay={setPlaying}
          />
        )}
      </main>

      {toast && (
        <div className={`toast ${toast.kind === 'err' ? 'err' : 'ok'}`} onClick={() => setToast(null)}>
          {toast.msg}
        </div>
      )}
    </div>
  )
}
